import {
  AsyncValidatorFn,
  UntypedFormControl,
  ValidationErrors,
  ValidatorFn,
} from '@angular/forms';

/**
 * Returns a Validator that checks the file size of the control
 *
 * @param maxSize - max size
 */
export function fileSizeValidator(maxSize: number): ValidatorFn {
  return (control: UntypedFormControl): ValidationErrors | undefined => {
    const file: File = control.value;
    const ERROR = { fileSize: true };
    if (file instanceof File) {
      if (file.size > maxSize) {
        return ERROR;
      }
    }
  };
}

/**
 * Returns a Validator that checks the mime type of the file and returns
 * an ValidationError if the File type does not match any in the given array
 *
 * @param allowedMimeTypes - array of mime types to match
 */
export function fileTypeValidator(allowedMimeTypes: string[]): ValidatorFn {
  return (control: UntypedFormControl): ValidationErrors | undefined => {
    const file: File = control.value;
    const ERROR = { fileType: true };
    if (file instanceof File) {
      if (!allowedMimeTypes.includes(file.type)) {
        return ERROR;
      }
    }
  };
}

const imageDimensionsCache = new WeakMap<File, Promise<{ width: number; height: number }>>();

function getCachedImageDimensions(file: File): Promise<{ width: number; height: number }> {
  if (imageDimensionsCache.has(file)) {
    return imageDimensionsCache.get(file)!;
  }

  const promise = new Promise<{ width: number; height: number }>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event: any) => {
      const img = new Image();
      img.onload = () => {
        resolve({ width: img.width, height: img.height });
      };
      img.onerror = () => {
        reject(new Error('Image load error'));
      };
      img.src = event.target.result;
    };
    reader.onerror = () => {
      reject(new Error('File read error'));
    };
    reader.readAsDataURL(file);
  });

  imageDimensionsCache.set(file, promise);
  return promise;
}

/**
 * Returns a Validator that checks if the image has the appropriate dimensions
 *
 * @param minWidth - minimum width
 * @param maxWidth - maximum width
 * @param minHeight - minimum height
 * @param maxHeight - max height
 */
export function imageDimensionsAsyncValidator(
  minWidth: number,
  maxWidth: number,
  minHeight: number,
  maxHeight: number,
): AsyncValidatorFn {
  return (control: UntypedFormControl): Promise<ValidationErrors | undefined> => {
    const file: File = control.value;
    const ERROR = { imageDimensions: true };
    const VALID = undefined;

    if (!(file instanceof File)) {
      return Promise.resolve(VALID);
    }

    return getCachedImageDimensions(file)
      .then((dims) => {
        if (
          dims.width < minWidth ||
          dims.width > maxWidth ||
          dims.height < minHeight ||
          dims.height > maxHeight
        ) {
          return ERROR;
        } else {
          return VALID;
        }
      })
      .catch(() => ERROR);
  };
}

export function fileAspectRatioValidator(
  requiredAspectRatio: number,
  tolerance: number = 0,
): AsyncValidatorFn {
  return (control: UntypedFormControl): Promise<ValidationErrors | undefined> => {
    const file: File = control.value;
    const ERROR = { imageAspectRatio: true };
    const VALID = undefined;

    if (!(file instanceof File)) {
      return Promise.resolve(VALID);
    }

    return getCachedImageDimensions(file)
      .then((dims) => {
        const actualAspectRatio = dims.width / dims.height;
        const minRatio = requiredAspectRatio - tolerance;
        const maxRatio = requiredAspectRatio + tolerance;
        // Check for aspect ratio match with tolerance
        if (actualAspectRatio < minRatio || actualAspectRatio > maxRatio) {
          return ERROR;
        } else {
          return VALID;
        }
      })
      .catch(() => ERROR);
  };
}
